import { Box, FormHelperText, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { BareAxios, hideGrecaptcha, mountGrecaptcha } from "../common/utils";
import { baseApi } from "../common/constants";
import { SignInCard, SignInContainer } from "./subComps/loginPieces";
import { H5 } from "./account/components/Typography";
import { Context } from "../common/context";
import type { Size, AppContext } from "../types";

const GetCode = ({ size, context }: { size: Size; context: AppContext }) => {
    const [code, setCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    const [loaded, setLoaded] = useState(false);
    const [showPage, setShowPage] = useState(false);
    const [expiredTimeout, setExpiredTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const data = searchParams.get(`data`);
    const navigate = useNavigate();

    useEffect(() => {
        const bc = new BroadcastChannel(`li_login`);
        BareAxios.post(`${baseApi}getcode`, { data })
            .then(c => {
                setCode(c.data.code);
                const timeLeft = 1000 * 15 * 60 - (Date.now() - new Date(c.data.ts).getTime());
                setExpiredTimeout(
                    setTimeout(
                        () => navigate(c.data.newUser ? `/signup` : `/login`, { state: { reason: `expired` } }),
                        timeLeft
                    )
                );
                localStorage.setItem(`sid`, c.data.sid);
                bc.postMessage(`code page loaded`);
                setLoaded(true);
            })
            .catch(e => {
                if (e.response.data?.reason === `expired`) {
                    clearTimeout(expiredTimeout ?? undefined);
                    navigate(e.response.data.newUser ? `/signup` : `/login`, { state: { reason: `expired` } });
                }
                setError(`Sorry, something went wrong. Please try again.`);
                setLoaded(true);
            });

        bc.onmessage = m => {
            if (m.data === `new user`) {
                clearTimeout(expiredTimeout ?? undefined);
                navigate(`/register`);
            }
            if (m.data === `old user`) {
                clearTimeout(expiredTimeout ?? undefined);
                navigate(`/`);
            }
        };

        mountGrecaptcha();
        return () => hideGrecaptcha();
    }, []);

    useEffect(() => {
        if (loaded) {
            setTimeout(() => {
                setShowPage(true);
            }, 500);
        }
    }, [loaded]);

    return showPage ? (
        <Context.Consumer>
            {value =>
                !value.context?.adminConfig ? null : (
                    <SignInContainer size={size} direction="column" justifyContent="space-between">
                        <Box
                            sx={{
                                margin: `0 auto ${size === `small` ? 0 : `24px`}`,
                            }}
                        >
                            <Box
                                sx={{ height: `auto`, width: `150px` }}
                                component="img"
                                src={context?.adminConfig?.mall?.Color_Desktop_Logo}
                            />
                        </Box>
                        <SignInCard size={size} variant="outlined">
                            {!value.context?.adminConfig?.mall?.enable_login_page && (
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{
                                        fontFamily: `Plus Jakarta Sans`,
                                        width: `100%`,
                                        fontSize: `36px`,
                                        fontWeight: 700,
                                        textAlign: `center`,
                                    }}
                                >
                                    This function is disabled
                                </Typography>
                            )}
                            {value.context?.adminConfig?.mall?.enable_login_page && (
                                <>
                                    <Typography
                                        component="h1"
                                        variant="h4"
                                        sx={{
                                            fontFamily: `Plus Jakarta Sans`,
                                            width: `100%`,
                                            fontSize: `36px`,
                                            fontWeight: 700,
                                            textAlign: `center`,
                                        }}
                                    >
                                        Verification Code
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: `flex`,
                                            flexDirection: `column`,
                                            width: `100%`,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                textAlign: `center`,
                                                fontSize: `18px`,
                                                marginTop: `8px`,
                                                marginBottom: `24px`,
                                            }}
                                        >
                                            We weren&apos;t able to instantly log you in. No worries, you can use this
                                            code to log in.
                                        </Typography>
                                        <H5
                                            id="li-code"
                                            sx={{ textAlign: `center`, fontSize: `40px`, margin: `20px 0` }}
                                        >
                                            {code}
                                        </H5>
                                        {error && (
                                            <FormHelperText
                                                error
                                                sx={{
                                                    mt: 1,
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    minHeight: `22px`,
                                                    minWidth: `2px`,
                                                    textAlign: `center`,
                                                }}
                                            >
                                                {error}
                                            </FormHelperText>
                                        )}
                                    </Box>
                                </>
                            )}
                        </SignInCard>
                        <Typography
                            sx={{ textAlign: `center`, fontSize: `13px`, marginTop: `20px`, padding: `0 20px` }}
                        >
                            Don&apos;t see where to enter your code? Click the link at the bottom of your other window.
                        </Typography>
                    </SignInContainer>
                )
            }
        </Context.Consumer>
    ) : null;
};

export default GetCode;
